import { Observable, ReplaySubject } from '@reactivex/rxjs';
import { asyncIteratorToObservable } from './util';
import { applyTickBatch } from './tickSink';
import mapPropsStream from 'react-recompose/mapPropsStream';
import * as graphAPI from '@graphistry/viz/src/client/api/graph';
import { readTicksViaWorker } from '@graphistry/viz/src/client/api/layoutTick';
import { createLogger } from '@graphistry/common/logger';
import { traceFields } from '@graphistry/common/traceparent';

const logger = createLogger(__filename);

export const withArrowData = mapPropsStream((props) => {

    console.info('withArrowData: initializing');
    const trail = window.trail;

    const graph = {
        version: 0,
        compression: 32,
        layoutOptions: {
            gravity: 1,
            linLog: false,
            lockedX: false,
            lockedY: false,
            lockedR: false,
            scalingRatio: 0,
            edgeInfluence: 0,
            dissuadeHubs: false,
            strongGravity: false,
            precisionVsSpeed: -0.1,
        }
    };

    return props
        .map(mergeLayoutSettingsAndOptions)
        .let(replay1())
        .flatMap(openGraphAPIWithSharedProps(trail))
        .flatMap(scanPropChangesAndTriggerGraphUpdates(trail))
        .catch((error) => {
            const maxRetries = 5;
            const maxDelayMS = 5000; // Cap the maximum delay before refresh to 5 seconds
            let retries = 0;
            let delayMS = 100; // Initial delay in milliseconds

            // Function to calculate the next delay using exponential backoff
            function getNextDelay(currentDelay) {
                const nextDelay = currentDelay * 1.5;
                return Math.min(nextDelay, maxDelayMS);
            }

            // Refresh the page using exponential backoff, test health until we have at least 1 healthy worker
            function refreshPage() {
                logger.error('Error occurred, will reload the page after the system is up again');
                const uri = `${graphAPI._API}/health/workers/gpu/internal`;
                fetch(uri).then(response => {
                    // Check response.ok before parsing - endpoint might return HTML error page
                    if (!response.ok) {
                        return { status: response.status, error: 'Health check endpoint not ready' };
                    }
                    return response.json();
                }).then(data => {
                    if (data.status === 200) {
                        console.info('At least 1 worker is ready for serving ... reloading the page!', {...traceFields(trail)});
                        location.reload();
                        return;
                    } else {
                        retries++;
                        delayMS = getNextDelay(delayMS);
                        if (retries <= maxRetries) {
                            logger.error({status: data.status, error: data.error, ...traceFields(trail)}, `Got an error ... retrying in ${delayMS}ms (attempt ${retries})`);
                            return new Promise(resolve => setTimeout(resolve, delayMS)).then(refreshPage);
                        } else {
                            logger.error({retries, maxRetries, ...traceFields(trail)}, 'Max retries exceeded, will reload the page!');
                            location.reload();
                        }
                    }
                }).catch(err => {
                    // Handle network errors or JSON parse failures
                    logger.error({err, ...traceFields(trail)}, 'Health check failed (network or parse error)');
                    retries++;
                    delayMS = getNextDelay(delayMS);
                    if (retries <= maxRetries) {
                        return new Promise(resolve => setTimeout(resolve, delayMS)).then(refreshPage);
                    } else {
                        logger.error({retries, maxRetries, ...traceFields(trail)}, 'Max retries exceeded after health check errors, reloading');
                        location.reload();
                    }
                });
            }

            return refreshPage();
          });

    function mergeLayoutSettingsAndOptions({ layout, ...props }) {
        const layoutOptions = graph.layoutOptions;
        try {
            const layoutSettingOptions = layout && layout.settings && layout.settings[0] || [];
            (layoutSettingOptions.forEach ? layoutSettingOptions : []).forEach(({ id, value }) => {
                (id in layoutOptions) && (layoutOptions[id] = value);
            });
        } catch (err) {
            logger.error(err, 'mergeLayoutSettingsAndOptions: failed to merge layout settings traceparent=%s', trail);
        }
        return { ...props, graph };
    }
});

function replay1(...xs) {
    return function letReplay1(x) {
        return x.multicast(() => new ReplaySubject(1), function share(x) {
            return Observable.of([...xs, x]).concat(Observable.never());
        });
    }
}

function openGraphAPIWithSharedProps(trail) { return ([props]) => {

    console.info('openGraphAPI: waiting for dataset to load', {...traceFields(trail)});

    return props
        .first(({ datasetLoaded }) => datasetLoaded)
        .do(() => { console.info('openGraphAPI: dataset loaded, calling /open', {...traceFields(trail)}); })
        .flatMap((x) =>
            // Flow: /open -> start SSE stream -> applyEncodings (async)
            // 1. /open gets initial data (with ETL-applied simple bindings)
            // 2. SSE stream connects to receive encoding update notifications
            // 3. applyEncodings triggers server-side complex encodings
            // 4. GPU sets encodingsUpdated=true and sends SSE message
            // 5. SSE triggers /read to fetch encoded data
            Observable.from(graphAPI.open(trail)(x))
                .catch((err) => { logger.error(err, 'openGraphAPI: /open failed traceparent=%s', trail); return Observable.throw(err); })
                .flatMap((openResult) => {
                    console.info('openGraphAPI: /open complete, starting SSE stream', { sessionId: openResult.session, encodingsApplied: openResult.encodingsApplied, ...traceFields(trail) });

                    // Start SSE stream FIRST, then trigger applyEncodings
                    // This ensures we receive the SSE notification when encodings are applied
                    const sseStream = dynamicGraphUpdates(trail)(openResult);

                    // ALWAYS fire applyEncodings - even for already-opened sessions.
                    // This ensures Falcor legend data is populated for histograms in ALL windows.
                    // Read window.trail fresh here rather than the mount-time capture (`trail`):
                    // window.trail is set asynchronously by the socket 'trail' event, and by the time
                    // applyEncodings fires (after /open) it is usually populated, so we forward the
                    // current graph.viz.load traceparent and keep applyEncodings in the load trace.
                    // Falls back to the captured trail. Pairs with the server-side root fallback.
                    const encTrail = window.trail || trail;
                    Observable.defer(() => Observable.from(graphAPI.applyEncodings(encTrail)(openResult)))
                        .subscribe({
                            next: () => console.info('openGraphAPI: applyEncodings completed', { sessionId: openResult.session, encodingsApplied: openResult.encodingsApplied, ...traceFields(encTrail) }),
                            error: (err) => logger.error(err, 'openGraphAPI: applyEncodings failed, using default encodings sessionId=%s traceparent=%s', openResult.session, encTrail)
                        });

                    return Observable.combineLatest(
                        Observable.of(openResult),
                        sseStream,
                        (x, graphUpdate) => ({ ...x, graphUpdate })
                    );
                }),
            (x, y) => ({ ...x, ...y })
        )
        .let(replay1(props.map(({ datasetLoaded, ...rest }) => rest)));
}}

function scanPropChangesAndTriggerGraphUpdates(trail) { return ([propsStream, firstRenderAndGraphUpdates]) => {

    const triggers = firstRenderAndGraphUpdates.take(1)
        .flatMap(selectInitialPlayAmount(propsStream, firstRenderAndGraphUpdates))
        .scan((x, y) => {
            const next = { ...x, ...y };
            return { ...next, simChanged: x.simulating !== next.simulating };
        }, { graphUpdate: null, simChanged: false, simulating: false });
    
    return triggers
        .switchMap(selectGraphAPIUpdateCommand(trail)).startWith({})
        .combineLatest(propsStream, (update, props) => ({ ...update, ...props }));
}}
    
function dynamicGraphUpdates(trail) { return ({ graph, ...props }) => {

    let updateCount = 0;
    const sessionId = props.session;

    return graphAPI.graphUpdatesObservable(trail)(props)
        .subscribeOn(undefined, 10)
        .retryWhen(errors => errors
            .do((err) => logger.warn({ err, sessionId, updateCount, ...traceFields(trail) }, 'dynamicGraphUpdates: SSE connection error, retrying'))
            .delay(1000)
            .take(10)
            .concat(Observable.throw(new Error('dynamicGraphUpdates: SSE failed all retries'))))
        .do((v) => {
            console.debug({ sessionId, updateCount, event: v, ...traceFields(trail) }, 'dynamicGraphUpdates: received SSE event');
            updateCount++;
        })
        .catch((err) => {
            logger.error(err, 'dynamicGraphUpdates: SSE stream failed sessionId=%s updateCount=%s traceparent=%s', sessionId, updateCount, trail);
            return Observable.empty();
        })
        .defaultIfEmpty({})
        .filter(({ type }) => type === 'message')
        .map((v) => +v.data || graph.version)
        .startWith(graph.version)
        .distinctUntilChanged()
        .skip(1).timestamp()
        .startWith(null);
}}

function selectGraphAPIUpdateCommand(trail) { return ({ graphUpdate, simChanged, ...props }) => {

    const graphUpdateTime = graphUpdate && graphUpdate.timestamp || 0;

    // by default do nothing
    let obs = Observable.of(props);

    // if the simulation state was changed or the server pushed a
    // new version, call the graph api to kick things off.
    if (simChanged || graphUpdateTime > 0) {

        // a bit of a hack -- mutate the state on the Rx Timestamp
        // instance so we don't call `graphAPI.load()` a second time
        // for the same "new version" event
        graphUpdateTime > 0 && (graphUpdate.timestamp = 0);

        obs = asyncIteratorToObservable(
            graphUpdateTime > 0 ? graphAPI.load(trail)(props)
             : props.simulating ? graphAPI.play(trail)(props)
                                : graphAPI.read(trail)(props)
        )
        // copy the version from the resulting event back on to the graph instance just in case
        .do((x) => (x.graph.version = graphUpdateTime > 0 ? graphUpdate.value : x.version || x.graph.version))
        // If we're stopping (simulating is false), concat a stop after the read
        .concat(
            !props.simulating && graphUpdateTime === 0
                ? asyncIteratorToObservable(graphAPI.stop(trail)(props))
                : Observable.empty()
        );

        // While simulating, concat the worker-backed tick stream (see
        // readTicksViaWorker). The filter predicate IS the apply: the sink
        // consumes tick batches into GL/host state and drops them from the
        // props stream; only per-tick summaries and structural results reach
        // React. finally() stops the worker session on unsubscribe -- the
        // generator's own cleanup can't run while parked awaiting a message.
        if (props.simulating) {
            obs = obs.concat(Observable.defer(() => {
                const ticks = readTicksViaWorker(trail)(props);
                return asyncIteratorToObservable(ticks.iterator)
                    .filter((emission) => !applyTickBatch(emission))
                    .finally(ticks.dispose);
            }).repeat());
        }
    }

    return obs;
}}

function selectInitialPlayAmount(propsStream, firstRenderAndGraphUpdates) {

    return function maybeAutoplayAfterGraphIsOpen({ play, ...initialGraphRenderProps }) {

        const { $falcor } = initialGraphRenderProps;
        const simulateChanges = propsStream.distinctUntilKeyChanged('simulating');
        return simulateChanges.multicast(() => new ReplaySubject(1), maybeAutoplay);
    
        function maybeAutoplay(simulateChanges) {
    
            // Merge simulateChanges with the graphUpdate events SSE channel.
            // Ignore the first value because firstRenderAndGraphUpdates is a
            // ReplaySubject(1). Filter firstRenderAndGraphUpdates on distinct
            // graphUpdate changes. A gpu-worker is on the other end of the SSE
            // channel and sends us tiny notifications that we need to refresh
            // our node and edge tables because something other than layout has
            // modified the graph. Examples of these include, filters, encodings,
            // and the dynamic graph API.
            const externalStimuli = simulateChanges.merge(
                firstRenderAndGraphUpdates.skip(1)
                    .distinctUntilKeyChanged('graphUpdate')
                    .map(({ graphUpdate }) => ({ graphUpdate })));
    
            switch (typeof play) {
                // If play === true or is positive, turn simulating on and autoplay
                case 'boolean': if (play) break;
                case 'number': if (play > 0) break;
                // If not, return the update streams starting with the initial render props
                default: return externalStimuli.startWith(initialGraphRenderProps);
            }
    
            const sceneModel = $falcor._clone({ _path: $falcor._path.slice(0, -1) });
    
            const toggleSimulating = (simulating) => sceneModel.set({
                json: { simulating, controls: { 0: { selected: simulating }}}
            }).map(({ json: { simulating } }) => ({ simulating }));
    
            // If play is true/a positive number, toggle simulating on and
            // kick off a race between the user (who may press the "stop" button) and a setTimeout
            // of `play` ms duration (if `play` is a positive number).

            const cancelAutoplay = simulateChanges.first((x) => !x || !x.simulating);

            // If play === true, play indefinitely. Otherwise stop after `play` ms.
            const endAutoplay = play === true
                ? Observable.never()
                : simulateChanges
                    .filter(x => x && x.simulating)
                    .take(1)
                    .flatMapTo(observableTimeout(play));

            // Take the first value from either user-input, or the (possible) autoplay timer
            const finishAutoplay = Observable.race(endAutoplay, cancelAutoplay).take(1);

            // Once the user or timer emits a value, this whole Observable turns back into a
            // pumpkin, by which I mean it becomes an Observable of external stimuli -- either
            // user-initiated simulateChange events, or server-sent Graph update notifications.
            const stopAutoplaying = finishAutoplay.flatMapTo(toggleSimulating(false));
            
            // Toggle simulating, then concat the future stop events
            const autoplaySequence = toggleSimulating(true).concat(stopAutoplaying);
            
            // Start with the initial-render props first, then toggle simulating on.
            // Wait for something to turn it off, either the user or the autoplay timeout Observable.
            // Once it's off, concat the rest of the values from the infinite externalStimuli Observable,
            // skipping the first. Since its sources are ReplaySubjects, their buffered values will be stale.
            return observableTimeout(25)
                .flatMapTo(autoplaySequence)
                .concat(externalStimuli.skip(1))
                .startWith(initialGraphRenderProps);
        }
    }
}


// 
// Use setTimeout instead of the AsyncScheduler because Chrome pauses setInterval* timeouts
// if we become a background tab. Because of this, we aren't notified the timeout has been
// reached, and will continue autoplaying until the tab is in the foreground again. No bueno!
// 
// * The AsyncScheduler uses setInterval to schedule timeouts and intervals.
// 
const observableTimeout = (dueTimeMs) => Observable.create((sink) => {
    let id = setTimeout(() => {
        id = null;
        sink.next(0);
        sink.complete();
    }, dueTimeMs);
    return () => {
        if (id !== null) {
            clearTimeout(id);
            id = null;
        }
    };
});
